import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  CardHeader,
  CardBody,
  Grid,
  Text,
  Image,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Center,
  useColorModeValue,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import { arabicToEnglish } from '../utils/numberConverter';
import CaseNavigation from './CaseNavigation';

const TraceManagement = () => {
  const { caseId, sampleId } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    samples,
    traces,
    addTrace,
    updateTrace,
    deleteTrace,
    canEditTrace,
    loadTracesBySample,
    loading,
    loadingTraces
  } = useApp();
  const toast = useToast();
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const dashedBorderColor = useColorModeValue('gray.300', 'gray.600');
  const dropActiveBg = useColorModeValue('primary.50', 'primary.900');
  const modalBg = useColorModeValue('white', 'gray.800');
  const labelStrongColor = useColorModeValue('gray.700', 'gray.200');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [editingTrace, setEditingTrace] = useState(null);
  const [deleteTraceId, setDeleteTraceId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({
    traceImage: null,
    traceImagePreview: '',
    status: 'Valid',
    decision: '',
    name: '',
    identityNumber: '',
    notes: ''
  });

  const revokeObjectUrlIfNeeded = (maybeUrl) => {
    if (typeof maybeUrl === 'string' && maybeUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(maybeUrl);
      } catch {
        // ignore
      }
    }
  };

  const currentCase = cases.find(c => String(c.id) === String(caseId));
  const currentSample = samples.find(s => String(s.id) === String(sampleId));
  const sampleTraces = traces.filter(t => String(t.sampleId) === String(sampleId));

  // Load traces when component mounts or sampleId changes
  useEffect(() => {
    if (sampleId) {
      // Check if we already have traces for this sample to avoid unnecessary reloads
      const existingTraces = traces.filter(t => String(t.sampleId) === String(sampleId));
      if (existingTraces.length === 0) {
        loadTracesBySample(sampleId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  useEffect(() => {
    // لا نعيد التوجيه إلا بعد انتهاء التحميل
    if (!loading && (!currentCase || !currentSample)) {
      // Restore last page when navigating back
      const savedPage = localStorage.getItem('cases_last_page');
      if (savedPage) {
        // Page will be restored when CaseManagement loads
      }
      navigate('/cases');
    }
  }, [currentCase, currentSample, loading, navigate]);

  // أثناء التحميل نعرض لا شيء
  if (loading) return null;

  if (!currentCase || !currentSample) return null;

  const handleOpenModal = (trace = null) => {
    // Clean up any previous blob preview before opening a new modal session
    revokeObjectUrlIfNeeded(formData.traceImagePreview);

    if (trace) {
      setEditingTrace(trace);
      setFormData({
        traceImage: null,
        traceImagePreview: trace.traceImage || '',
        status: trace.status || 'Valid',
        decision: trace.decision || '',
        name: trace.name || '',
        identityNumber: trace.identityNumber || '',
        notes: trace.notes || ''
      });
    } else {
      setEditingTrace(null);
      setFormData({
        traceImage: null,
        traceImagePreview: '',
        status: 'Valid',
        decision: '',
        name: '',
        identityNumber: '',
        notes: ''
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    revokeObjectUrlIfNeeded(formData.traceImagePreview);
    onClose();
    setEditingTrace(null);
    setFormData({
      traceImage: null,
      traceImagePreview: '',
      status: 'Valid',
      decision: '',
      name: '',
      identityNumber: '',
      notes: ''
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  const processImageFile = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'خطأ',
        description: 'الملف المحدد ليس صورة',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'خطأ',
        description: 'حجم الصورة يجب أن يكون أقل من 10MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Replace any previous blob preview
    revokeObjectUrlIfNeeded(formData.traceImagePreview);
    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      traceImage: file,
      traceImagePreview: previewUrl
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processImageFile(files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const traceData = new FormData();
      traceData.append('caseId', caseId);
      traceData.append('sampleId', sampleId);
      traceData.append('status', formData.status);
      if (formData.decision) traceData.append('decision', formData.decision);
      if (formData.name) traceData.append('name', formData.name);
      if (formData.identityNumber) traceData.append('identityNumber', formData.identityNumber);
      if (formData.notes) traceData.append('notes', formData.notes);
      if (formData.traceImage) {
        traceData.append('traceImage', formData.traceImage);
      }

      if (editingTrace) {
        await updateTrace(editingTrace.id, traceData);
        toast({
          title: 'تم التحديث',
          description: 'تم تحديث الأثر بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await addTrace(traceData);
        toast({
          title: 'تمت الإضافة',
          description: 'تم إضافة الأثر بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving trace:', error);
      // Extract error message from Laravel validation errors
      let errorMessage = 'فشل في حفظ الأثر';
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          // Laravel validation errors format
          const errors = error.response.data.errors;
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast({
        title: 'خطأ',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteTraceId(id);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteTrace(deleteTraceId);
      toast({
        title: 'تم الحذف',
        description: 'تم حذف الأثر بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setDeleteTraceId(null);
    } catch (error) {
      console.error('Error deleting trace:', error);
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || error.message || 'فشل في حذف الأثر',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const statusLabels = {
    'Valid': 'صالح',
    'Invalid': 'غير صالح'
  };

  const decisionLabels = {
    'Match': 'تطابق',
    'Mismatch': 'عدم تطابق'
  };

  const getStatusColorScheme = (status) => {
    const colors = {
      'Valid': 'green',
      'Invalid': 'red'
    };
    return colors[status] || 'gray';
  };

  return (
    <Box>
      <CaseNavigation />
      <VStack align="stretch" spacing={4} mb={6}>
        <Button
          onClick={() => {
            // Save current page before navigating (if we have it)
            const savedPage = localStorage.getItem('cases_last_page');
            if (savedPage) {
              // Keep the saved page
            }
            navigate(`/samples/${caseId}`);
          }}
          variant="outline"
          size="sm"
          alignSelf="flex-start"
        >
          ← العودة إلى العينات
        </Button>
        <Flex justify="space-between" align="center">
          <Heading size="lg">إدارة آثار البصمات</Heading>
          {canEditTrace() && (
            <Button colorScheme="primary" onClick={() => handleOpenModal()}>
              إضافة أثر جديد
            </Button>
          )}
        </Flex>
      </VStack>

      <Card mb={6}>
        <CardBody>
          <VStack align="stretch" spacing={2}>
            <Heading size="md" mb={2}>معلومات العينة</Heading>
            <Text><strong>رقم القضية:</strong> <span dir="ltr" style={{ display: 'inline-block' }}>{currentCase.caseNumber}</span></Text>
            <Text><strong>رقم العينة:</strong> <span dir="ltr" style={{ display: 'inline-block' }}>{currentSample.sampleNumber}</span></Text>
            <Text><strong>الوصف:</strong> {currentSample.description}</Text>
          </VStack>
        </CardBody>
      </Card>

      {loadingTraces ? (
        <Center py={10}>
          <VStack spacing={4}>
            <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
            <Text color="gray.600">جاري تحميل الآثار...</Text>
          </VStack>
        </Center>
      ) : sampleTraces.length === 0 ? (
        <Card>
          <CardBody>
            <Text textAlign="center" color="gray.500">
              لا توجد آثار لهذه العينة
            </Text>
          </CardBody>
        </Card>
      ) : (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
          {sampleTraces.map((trace) => (
            <Card key={trace.id} boxShadow="md">
              <CardHeader>
                {canEditTrace() && (
                  <Flex justify="flex-end">
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(trace)}
                      >
                        تعديل
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteClick(trace.id)}
                      >
                        حذف
                      </Button>
                    </HStack>
                  </Flex>
                )}

                <Heading mt={canEditTrace() ? 3 : 0} size="xs" color="primary.600" dir="ltr" textAlign="left">
                  {trace.traceNumber}
                </Heading>
              </CardHeader>
              {trace.traceImage && (
                <Box p={4}>
                  <Image
                    src={trace.traceImage}
                    alt="أثر"
                    borderRadius="md"
                    maxH="200px"
                    mx="auto"
                    objectFit="contain"
                  />
                </Box>
              )}
              <CardBody>
                <VStack align="stretch" spacing={2}>
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      الحالة:
                    </Text>
                    <Badge colorScheme={getStatusColorScheme(trace.status)}>
                      {statusLabels[trace.status] || trace.status}
                    </Badge>
                  </Flex>
                  {trace.decision && (
                    <Flex justify="space-between" align="center">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                        القرار:
                      </Text>
                      <Text fontSize="sm">{decisionLabels[trace.decision] || trace.decision}</Text>
                    </Flex>
                  )}
                  {trace.name && (
                    <Flex justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                        الاسم:
                      </Text>
                      <Text fontSize="sm">{trace.name}</Text>
                    </Flex>
                  )}
                  {trace.identityNumber && (
                    <Flex justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                        رقم الهوية:
                      </Text>
                      <Text fontSize="sm" dir="ltr" textAlign="left">{trace.identityNumber}</Text>
                    </Flex>
                  )}
                  {trace.notes && (
                    <Flex justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                        ملاحظات:
                      </Text>
                      <Text fontSize="sm">{trace.notes}</Text>
                    </Flex>
                  )}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent bg={modalBg}>
          <ModalHeader>
            {editingTrace ? 'تعديل أثر' : 'إضافة أثر جديد'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>صورة الأثر</FormLabel>
                  <Box
                    border="2px dashed"
                    borderColor={isDragging ? 'primary.500' : dashedBorderColor}
                    borderRadius="md"
                    p={4}
                    textAlign="center"
                    cursor="pointer"
                    bg={isDragging ? dropActiveBg : 'transparent'}
                    _hover={{
                      borderColor: 'primary.500',
                      bg: dropActiveBg,
                    }}
                    transition="all 0.2s"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      display="none"
                      id="trace-image-upload"
                    />
                    <Box
                      as="label"
                      htmlFor="trace-image-upload"
                      cursor="pointer"
                      display="block"
                    >
                      <VStack spacing={2}>
                        <Text color={mutedTextColor} fontSize="sm" fontWeight={isDragging ? 'semibold' : 'normal'}>
                          {isDragging 
                            ? 'أفلت الصورة هنا' 
                            : formData.traceImagePreview 
                            ? 'تغيير الصورة' 
                            : 'انقر لاختيار صورة أو اسحبها هنا'}
                        </Text>
                        {!formData.traceImagePreview && (
                          <Text fontSize="xs" color={subtleTextColor}>
                            PNG, JPG, GIF حتى 10MB
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  </Box>
                  {formData.traceImagePreview && (
                    <Box mt={4} borderRadius="md" overflow="hidden" border="1px solid" borderColor={borderColor} position="relative">
                      <Image
                        src={formData.traceImagePreview}
                        alt="معاينة"
                        maxH="300px"
                        mx="auto"
                        objectFit="contain"
                      />
                      <Button
                        size="xs"
                        colorScheme="red"
                        position="absolute"
                        top={2}
                        right={2}
                        onClick={() => setFormData({ ...formData, traceImagePreview: '', traceImage: null })}
                      >
                        حذف
                      </Button>
                    </Box>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color={labelStrongColor}>
                    حالة الأثر
                  </FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    size="lg"
                    focusBorderColor="primary.500"
                  >
                    <option value="Valid">صالح</option>
                    <option value="Invalid">غير صالح</option>
                  </Select>
                </FormControl>

                {formData.status === 'Valid' && (
                  <>
                    <FormControl>
                      <FormLabel fontWeight="semibold" mb={2} color={labelStrongColor}>
                        القرار
                      </FormLabel>
                      <Select
                        value={formData.decision}
                        onChange={(e) =>
                          setFormData({ ...formData, decision: e.target.value })
                        }
                        size="lg"
                        focusBorderColor="primary.500"
                      >
                        <option value="">اختر القرار</option>
                        <option value="Match">تطابق</option>
                        <option value="Mismatch">عدم تطابق</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>الاسم</FormLabel>
                      <Input
                        value={formData.name}
                        onChange={(e) => {
                          const convertedValue = arabicToEnglish(e.target.value);
                          setFormData({ ...formData, name: convertedValue });
                        }}
                        placeholder="أدخل الاسم"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>رقم الهوية</FormLabel>
                      <Input
                        value={formData.identityNumber}
                        onChange={(e) => {
                          const convertedValue = arabicToEnglish(e.target.value);
                          setFormData({ ...formData, identityNumber: convertedValue });
                        }}
                        placeholder="أدخل رقم الهوية"
                      />
                    </FormControl>
                  </>
                )}

                <FormControl>
                  <FormLabel>ملاحظات (اختياري)</FormLabel>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={4}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <Box p={6} borderTop="1px solid" borderColor={borderColor}>
              <HStack justify="flex-end" spacing={3}>
                <Button variant="outline" onClick={handleCloseModal}>
                  إلغاء
                </Button>
                <Button type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText={editingTrace ? 'جاري الحفظ...' : 'جاري الإضافة...'}>
                  {editingTrace ? 'حفظ التعديلات' : 'إضافة'}
                </Button>
              </HStack>
            </Box>
          </form>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} leastDestructiveRef={undefined}>
        <AlertDialogOverlay>
          <AlertDialogContent bg={modalBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              حذف الأثر
            </AlertDialogHeader>
            <AlertDialogBody>
              هل أنت متأكد من حذف هذا الأثر؟
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                إلغاء
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} mr={3} isLoading={isDeleting} loadingText="جاري الحذف...">
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TraceManagement;
